import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { Badge } from './badge';
import { Input } from './input';
import { Select } from './select';
import { Switch } from './switch';
import { TagsInput } from './tags-input';
import type { FieldDraft } from '@/lib/forms';
import type { FormFieldCatalogData } from '@/types/api';

/**
 * Editor for a form's field map.
 *
 * Laid out as a grid rather than a card per field: eight discovered fields used
 * to be roughly 950px of scrolling with every control labelled eight times
 * over. Column headers label each control once, which is also what fixes the
 * Switch — it renders its `label` prop only as an aria-label, so a toggle
 * outside a labelled column was visually anonymous.
 *
 * Shell follows the journal entry editor (routes/accounting/journal.tsx) and
 * the cell treatment follows the invoice line-item builder
 * (routes/clients/tabs/invoices.tsx). The type and role lists come from the
 * served catalog, so a new enum case appears here with no change to this file.
 */

const GRID = 'grid grid-cols-[minmax(130px,1fr)_minmax(130px,1fr)_140px_170px_52px_36px] gap-2';

export function FormFieldRows({
  rows,
  onChange,
  catalog,
  samples = {},
}: {
  rows: FieldDraft[];
  onChange: (next: FieldDraft[]) => void;
  catalog: FormFieldCatalogData | undefined;
  /** One received value per key, so you can see what you are mapping. */
  samples?: Record<string, string>;
}) {
  function update(index: number, patch: Partial<FieldDraft>) {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function remove(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  function add() {
    onChange([
      ...rows,
      {
        key: '',
        label: '',
        type: 'text',
        role: '',
        required: false,
        include_in_summary: false,
        options: [],
        discovered: false,
      },
    ]);
  }

  const types = catalog?.types ?? [];
  const typeGroups = [...new Set(types.map((t) => t.group))];
  const roles = catalog?.roles ?? [];
  const roleGroups = [...new Set(roles.map((r) => r.group))];

  // A role can only be claimed once, so hide the ones already taken.
  const claimed = new Set(rows.map((r) => r.role).filter(Boolean));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[760px] overflow-hidden rounded-md border border-[var(--color-border)]">
        <div
          className={`${GRID} border-b border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]`}
        >
          <div>Field</div>
          <div>Label</div>
          <div>Type</div>
          <div>Means</div>
          <div className="text-center">Column</div>
          <div />
        </div>

        {rows.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-[var(--color-foreground-muted)]">
            No fields mapped yet. Submissions are stored in full either way — mapping just adds
            labels and types.
          </p>
        ) : null}

        {rows.map((row, i) => {
          const type = types.find((t) => t.key === row.type);
          const sample = samples[row.key];

          return (
            <div
              key={i}
              className={`${GRID} items-center border-b border-[var(--color-border)] px-3 py-2 last:border-b-0`}
            >
              {/* Their key. Read-only once discovered: editing it silently
                  unmaps the field from incoming data. */}
              {row.discovered === false ? (
                <Input
                  value={row.key}
                  onChange={(e) => update(i, { key: e.target.value })}
                  placeholder="your-email"
                  className="h-8 font-mono text-xs"
                />
              ) : (
                <Badge tone="neutral" className="justify-start truncate font-mono" title={row.key}>
                  {row.key}
                </Badge>
              )}

              <Input
                value={row.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder="Email address"
                className="h-8 text-xs"
                aria-label={`Label for ${row.key}`}
              />

              <Cell detected={row.detectedType}>
                <Select
                  value={row.type}
                  onChange={(e) => update(i, { type: e.target.value, detectedType: false })}
                  className="h-8 text-xs"
                  aria-label={`Type for ${row.key}`}
                  title={type?.description}
                >
                  {typeGroups.map((g) => (
                    <optgroup key={g} label={g}>
                      {types
                        .filter((t) => t.group === g)
                        .map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.label}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              </Cell>

              <Cell detected={row.detectedRole}>
                <Select
                  value={row.role ?? ''}
                  onChange={(e) => update(i, { role: e.target.value, detectedRole: false })}
                  className="h-8 text-xs"
                  aria-label={`Means for ${row.key}`}
                >
                  <option value="">Nothing special</option>
                  {roleGroups.map((g) => (
                    <optgroup key={g} label={g}>
                      {roles
                        .filter((r) => r.group === g)
                        .filter((r) => r.key === row.role || !claimed.has(r.key))
                        .map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.label}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              </Cell>

              <div
                className="flex justify-center"
                title="Give this answer its own column in the form inbox"
              >
                <Switch
                  checked={row.include_in_summary ?? false}
                  onChange={(include_in_summary) => update(i, { include_in_summary })}
                  label={`Give ${row.key} its own column in the form inbox`}
                />
              </div>

              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${row.label || row.key}`}
                className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
              >
                <Trash2 className="size-4" />
              </button>

              {sample ? (
                <p className="col-span-6 -mt-0.5 truncate text-xs text-[var(--color-foreground-subtle)]">
                  <span className="text-[var(--color-foreground-muted)]">e.g.</span>{' '}
                  <span className="font-mono">{sample}</span>
                </p>
              ) : null}

              {type?.supports_options ? (
                <div className="col-span-6 space-y-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
                    Allowed choices
                  </span>
                  <TagsInput
                    value={row.options ?? []}
                    onChange={(options) => update(i, { options })}
                  />
                </div>
              ) : null}
            </div>
          );
        })}

        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
          >
            <Plus className="size-3" /> Add field
          </button>
          <p className="text-[11px] text-[var(--color-foreground-subtle)]">
            <span className="font-medium">Column</span> puts that answer in the form inbox table,
            first four win. Name and message already have columns there.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Wraps a control, flagging it while it still holds a detected value. */
function Cell({ detected, children }: { detected?: boolean; children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      {detected ? (
        <Sparkles
          className="pointer-events-none absolute -right-1 -top-1 size-3 text-[var(--color-primary)]"
          aria-label="Detected from received values"
        />
      ) : null}
    </div>
  );
}
